import { createTheme, ThemeProvider } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import AttorneyHubLogo from "_shared/_views/assets/attorney-hub-logo.svg";
import { useGetUserEntityMemberInvitationByIdQuery } from "legal-entities/invitations/member-invitations/email-based/_api/user-entity-member-invitation-api-service";
import UserEntityMemberInvitationAcceptanceForm from "legal-entities/invitations/member-invitations/email-based/_views/forms/user-entity-member-invitation-acceptance-form";
import { UserMemberInvitationNotFoundError } from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router";

const FormContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#404558",
  boxSizing: "border-box",
  height: "100%",
  overflow: "auto",
  padding: theme.spacing(5, 1),
  textAlign: "center",
  width: "100%",
}));
const Logo = styled("img")(({ theme }) => ({
  marginBottom: theme.spacing(4),
  maxWidth: "90%",
}));
const InvitationForm = styled(Container)(({ theme }) => ({
  color: theme.palette.common.white,
  padding: theme.spacing(2),
  position: "relative",
  width: "100%",
}));
const InvitationFormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));
const InvitationFormInstructions = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  marginBottom: theme.spacing(2),
  "& a": {
    color: theme.palette.common.white,
    textDecoration: "underline",
  },
}));

type UserEntityMemberInvitationAcceptancePageProps = {
  className?: string;
  invitationId?: Guid;
};

export default function UserEntityMemberInvitationAcceptancePage(
  props: Readonly<UserEntityMemberInvitationAcceptancePageProps>,
) {
  const { className } = props;

  const params = useParams<{ invitationId: string }>();
  const theme = createTheme();

  const invitationId = useMemo(
    () => props.invitationId ?? (params.invitationId ? new Guid(params.invitationId) : undefined),
    [params.invitationId, props.invitationId],
  );
  const { data: invitation, error, isFetching, isLoading, isUninitialized } = useGetUserEntityMemberInvitationByIdQuery(
    invitationId ? { id: invitationId } : skipToken,
  );
  const loading = Boolean(invitationId) && (isUninitialized || isLoading || isFetching);
  const invitationNotFound = !invitationId || error instanceof UserMemberInvitationNotFoundError;
  const invitationLoadError = Boolean(error) && !invitationNotFound;

  useEffect(() => {
    if (!invitationId) console.error(new Error("Invitation ID was not provided"));
  }, [invitationId]);

  useEffect(() => {
    if (!error) return;
    if (error instanceof UserMemberInvitationNotFoundError) {
      console.warn(error.message);
      return;
    }
    console.error(error);
  }, [error]);

  function invitationNotFoundMessage() {
    return (
      <InvitationForm className={className}>
        <InvitationFormTitle variant="h4">Invitation Not Found</InvitationFormTitle>
        <InvitationFormInstructions>
          The invitation couldn&apos;t be found.
          <br />
          If you believe this is in error please contact{" "}
          <a href="mailto:support@attorneyhub.com" target="_blank" rel="noreferrer">
            support@attorneyhub
          </a>
          .<br />
          You can close this tab
        </InvitationFormInstructions>
      </InvitationForm>
    );
  }

  function invitationErrorMessage() {
    return (
      <InvitationForm className={className}>
        <InvitationFormTitle variant="h4">Invitation Error</InvitationFormTitle>
        <InvitationFormInstructions>
          The invitation couldn&apos;t be loaded.
          <br />
          Please try again. If the problem continues, contact{" "}
          <a href="mailto:support@attorneyhub.com" target="_blank" rel="noreferrer">
            support@attorneyhub
          </a>
          .
        </InvitationFormInstructions>
      </InvitationForm>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <FormContainer className={className}>
        <Logo src={AttorneyHubLogo} alt="Attorney Hub Logo" />
        {loading && <Loader />}
        {!invitation && !loading && invitationNotFound && invitationNotFoundMessage()}
        {!invitation && !loading && invitationLoadError && invitationErrorMessage()}
        {invitation && <UserEntityMemberInvitationAcceptanceForm invitation={invitation} />}
      </FormContainer>
    </ThemeProvider>
  );
}
